import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, CheckCircle2, Calculator, Receipt, Send, TrendingUp, Banknote, Zap } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  LabelList,
} from 'recharts';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  faturas: any[];
  mesesDisponiveis: { ano: string; meses: { label: string }[] }[];
}

const PIPELINE = [
  { key: 'Fatura disponível',              label: 'Subidas',   color: '#8b5cf6', icon: FileText },
  { key: 'Fatura validada',                label: 'Validadas', color: '#3b82f6', icon: CheckCircle2 },
  { key: 'Com boleto calculado',           label: 'Calculados',color: '#f59e0b', icon: Calculator },
  { key: 'Com boleto emitido / A enviar',  label: 'Emitidos',  color: '#5F6C37', icon: Receipt },
  { key: 'Cobrança enviada',               label: 'Enviadas',  color: '#10b981', icon: Send },
];

const fmtBRL = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 shadow-xl text-xs">
      <p className="font-bold text-gray-800 dark:text-white mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color }} className="font-semibold">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

const FinanceiroKPIDashboard: React.FC<Props> = ({ isOpen, onClose, faturas, mesesDisponiveis }) => {
  const pipeline = useMemo(() =>
    PIPELINE.map(p => ({ ...p, count: faturas.filter(f => f.status_fatura === p.key).length })),
    [faturas]
  );

  const canceladas = useMemo(
    () => faturas.filter(f => f.cancelado_em).length,
    [faturas]
  );

  const totalValor = useMemo(
    () => faturas.reduce((s, f) => s + (Number(f.valor_boleto_vigor) || 0), 0),
    [faturas]
  );

  const totalEconomia = useMemo(
    () => faturas.reduce((s, f) => s + (Number(f.economia) || 0), 0),
    [faturas]
  );

  const monthlyData = useMemo(() => {
    const map: Record<string, { mes: string; subidas: number; emitidos: number; enviadas: number; valor: number }> = {};
    faturas.forEach(f => {
      const m = f.mes_referencia || 'Sem mês';
      if (!map[m]) map[m] = { mes: m, subidas: 0, emitidos: 0, enviadas: 0, valor: 0 };
      map[m].subidas++;
      if (f.status_fatura === 'Com boleto emitido / A enviar' || f.status_fatura === 'Cobrança enviada') map[m].emitidos++;
      if (f.status_fatura === 'Cobrança enviada') map[m].enviadas++;
      map[m].valor += Number(f.valor_boleto_vigor) || 0;
    });

    const monthOrder: Record<string, number> = {
      JAN: 1, FEV: 2, MAR: 3, ABR: 4, MAI: 5, JUN: 6,
      JUL: 7, AGO: 8, SET: 9, OUT: 10, NOV: 11, DEZ: 12,
    };
    return Object.values(map).sort((a, b) => {
      const [amStr, ayStr] = a.mes.split('/');
      const [bmStr, byStr] = b.mes.split('/');
      const ay = Number(ayStr || 0), by = Number(byStr || 0);
      if (ay !== by) return ay - by;
      return (monthOrder[amStr] || 0) - (monthOrder[bmStr] || 0);
    });
  }, [faturas]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex flex-col bg-gray-50 dark:bg-gray-900 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-xl">
            <TrendingUp className="text-primary" size={22} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Dashboard Financeiro</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">{faturas.length} faturas no filtro atual</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* Pipeline Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {pipeline.map(p => {
            const Icon = p.icon;
            const pct = faturas.length > 0 ? ((p.count / faturas.length) * 100).toFixed(0) : '0';
            return (
              <div key={p.key} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-1.5 rounded-lg" style={{ backgroundColor: p.color + '20' }}>
                    <Icon size={14} style={{ color: p.color }} />
                  </div>
                  <span className="text-[10px] font-bold text-gray-400">{pct}%</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{p.count}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{p.label}</p>
              </div>
            );
          })}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="p-1.5 rounded-lg bg-red-50 dark:bg-red-900/20">
                <X size={14} className="text-red-500" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{canceladas}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Canceladas</p>
          </div>
        </div>

        {/* Financial Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <Banknote className="text-primary" size={16} />
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total a Receber</p>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{fmtBRL(totalValor)}</p>
            <p className="text-xs text-gray-400 mt-1">Soma dos boletos emitidos</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="text-green-500" size={16} />
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Economia</p>
            </div>
            <p className="text-xl font-bold text-green-600 dark:text-green-400">{fmtBRL(totalEconomia)}</p>
            <p className="text-xs text-gray-400 mt-1">Gerada para os clientes</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="text-blue-500" size={16} />
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Ticket Médio</p>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {fmtBRL(faturas.length > 0 ? totalValor / faturas.length : 0)}
            </p>
            <p className="text-xs text-gray-400 mt-1">Por fatura</p>
          </div>
        </div>

        {/* Pipeline Funnel Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4">Funil de Pipeline</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={pipeline} layout="vertical" margin={{ left: 16, right: 40, top: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <YAxis type="category" dataKey="label" width={90} tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Faturas" radius={[0, 6, 6, 0]} barSize={28}>
                {pipeline.map(p => (
                  <Cell key={p.key} fill={p.color} />
                ))}
                <LabelList dataKey="count" position="right" style={{ fontSize: 11, fontWeight: 700, fill: '#6b7280' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Evolution */}
        {monthlyData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4">Evolução Mensal</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyData} margin={{ left: 0, right: 0, top: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="mes" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="subidas" name="Subidas" fill="#8b5cf6" radius={[4, 4, 0, 0]} barSize={18} />
                <Bar dataKey="emitidos" name="Emitidos" fill="#5F6C37" radius={[4, 4, 0, 0]} barSize={18} />
                <Bar dataKey="enviadas" name="Enviadas" fill="#10b981" radius={[4, 4, 0, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-4 mt-3 justify-center">
              {[
                { color: '#8b5cf6', label: 'Subidas' },
                { color: '#5F6C37', label: 'Emitidos' },
                { color: '#10b981', label: 'Enviadas' },
              ].map(l => (
                <div key={l.label} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ background: l.color }} />
                  <span className="text-xs text-gray-500 dark:text-gray-400">{l.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>,
    document.body
  );
};

export default FinanceiroKPIDashboard;
